import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { wz } from '../../components/wizard-tokens';
import { WizGradientButton, WizInput, WizLink } from '../../components/wizard-ui';
import { FlowRow } from '../../campaign-wizard.types';
import {
  ACCEPT_WINDOW_KEY,
  checkWaitMinutes,
  formatWaitParts,
  joinWaitMinutes,
  minWaitMinutes,
  parseWaitPart,
  splitWaitMinutes,
  WaitParts,
  WaitUnits,
} from './flow-view.logic';

interface Props {
  /** Die angeklickte Zeile (Wartezeit oder „Kontaktanfrage akzeptiert?“); null = zu. */
  row: FlowRow | null;
  onClose: () => void;
  /** Neue Zeit in Minuten (nur gültige Werte). */
  onSave: (waitKey: string, minutes: number, defaultMinutes: number) => void;
}

/**
 * Wartezeit einstellen: Tage, Stunden, Minuten wie im Wartezeit-Knoten des alten Flow-Builders (PAI);
 * dieselben Umrechnungen (`splitTimeInSeconds` / `convertToSeconds`).
 */
const WaitTimeDialog: React.FC<Props> = ({ row, onClose, onSave }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [parts, setParts] = useState<WaitParts>({ days: 0, hours: 0, minutes: 0 });
  const waitKey = row?.waitKey ?? '';
  const defaultMinutes = row?.defaultMinutes ?? row?.minutes ?? 0;
  const units = { days: t('wizWaitDays'), hours: t('wizWaitHours'), minutes: t('wizWaitMinutes') };
  // Im Text („mindestens 1 Tag“) mit Einzahl bei 1; die Feldbeschriftungen bleiben im Plural.
  const textUnits: WaitUnits = {
    days: { one: t('wizWaitDay'), other: units.days },
    hours: { one: t('wizWaitHour'), other: units.hours },
    minutes: { one: t('wizWaitMinute'), other: units.minutes },
  };

  useEffect(() => {
    if (row) setParts(splitWaitMinutes(row.minutes ?? 0));
  }, [row]);

  const total = joinWaitMinutes(parts);
  const check = checkWaitMinutes(waitKey, total);
  const setPart = (key: keyof WaitParts) => (raw: string) => setParts(prev => ({ ...prev, [key]: parseWaitPart(raw) }));

  const submit = () => {
    if (check === 'ok' && row) onSave(waitKey, total, defaultMinutes);
  };

  return (
    <Dialog
      open={row !== null}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>
        {waitKey === ACCEPT_WINDOW_KEY ? t('wizWaitAcceptTitle') : t('wizWaitTitle')}
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px !important' }}
      >
        <div style={{ fontSize: 15, color: wz.muted }}>{t('wizWaitHint')}</div>
        <div style={{ display: 'flex', gap: 12 }}>
          {(['days', 'hours', 'minutes'] as const).map(key => (
            <label key={key} className={classes.field}>
              <span className={classes.fieldLabel}>{units[key]}</span>
              <WizInput
                small
                type="number"
                min={0}
                value={String(parts[key])}
                onChange={setPart(key)}
                invalid={check !== 'ok'}
                aria-label={units[key]}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submit();
                  }
                }}
              />
            </label>
          ))}
        </div>
        {check === 'tooShort' ? (
          <div className={classes.error}>
            {t('wizWaitTooShort', { min: formatWaitParts(minWaitMinutes(waitKey), textUnits) })}
          </div>
        ) : null}
        {check === 'tooLong' ? <div className={classes.error}>{t('wizWaitTooLong')}</div> : null}
        <div style={{ fontSize: 13, color: wz.muted, display: 'flex', gap: 8, alignItems: 'center' }}>
          {t('wizWaitDefault', { value: formatWaitParts(defaultMinutes, textUnits) })}
          {total !== defaultMinutes ? (
            <WizLink onClick={() => setParts(splitWaitMinutes(defaultMinutes))}>
              {t('wizWaitReset')}
            </WizLink>
          ) : null}
        </div>
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 20px', gap: '12px' }}>
        <button type="button" className={classes.cancel} onClick={onClose}>
          {t('wizWaitCancel')}
        </button>
        <WizGradientButton onClick={submit} disabled={check !== 'ok'}>
          {t('wizWaitSave')}
        </WizGradientButton>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()(() => ({
  field: { display: 'flex', flexDirection: 'column', gap: 4, flex: 1 },
  fieldLabel: { fontSize: 13, color: wz.muted },
  error: { fontSize: 13, color: wz.redFg },
  cancel: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 14,
    color: wz.muted,
    padding: '8px 12px',
  },
}));

export default WaitTimeDialog;
